import { Component } from '@theme/component';
import { fetchConfig, debounce, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

let cartRecoveryStarted = false;

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  #onQuantityChange(event) {
    if (!this.isConnected || !(event.target instanceof Element) ||
        event.target.closest('cart-items-component') !== this) return;
    const { quantity, cartLine: line } = event.detail;
    if (!Number.isSafeInteger(quantity) || quantity < 0) return;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

  }

  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).slice(0, 5).join(','),
      sections_url: window.location.pathname + window.location.search,
    });

    cartTotal?.shimmer();

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(async (response) => {
        const cart = await response.json();
        if (!cart || typeof cart !== 'object') throw new Error('Invalid cart response');

        if (!response.ok || cart.errors || cart.status) {
          const message = typeof cart.errors === 'string' ? cart.errors : cart.description;
          if (response.status === 422 && typeof message === 'string' && message.trim()) {
            this.#handleCartError(line, { errors: message });
            return;
          }
          throw new Error('Cart update could not be confirmed');
        }

        if (!Number.isSafeInteger(cart.item_count) || cart.item_count < 0) {
          throw new Error('Invalid cart item count');
        }

        const sections = cart.sections && typeof cart.sections === 'object' && !Array.isArray(cart.sections)
          ? cart.sections : {};
        this.dispatchEvent(
          new CartUpdateEvent(cart, this.sectionId, {
            itemCount: cart.item_count,
            source: 'cart-items-component',
            sections,
          })
        );
        await this.#renderCartSection(sections[this.sectionId]);
      })
      .catch(() => {
        this.#recoverCartPage();
      })
      .finally(() => {
        resetShimmer(this);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  #handleCartUpdate = async (event) => {
    if (!this.isConnected || event.target === this) return;
    try {
      const html = event instanceof DiscountUpdateEvent ? null : event.detail?.data?.sections?.[this.sectionId];
      await this.#renderCartSection(html);
    } catch {
      this.#recoverCartPage();
    }
  };

  async #renderCartSection(html) {
    const sectionHTML = typeof html === 'string' && html.trim()
      ? html
      : await sectionRenderer.getSectionHTML(this.sectionId, false);
    await morphSection(this.sectionId, sectionHTML);
  }

  #recoverCartPage() {
    if (cartRecoveryStarted) return;
    cartRecoveryStarted = true;
    const url = new URL(Theme.routes.cart_url, window.location.origin);
    const previewId = new URLSearchParams(window.location.search).get('preview_theme_id');
    if (previewId) url.searchParams.set('preview_theme_id', previewId);
    window.location.assign(url.pathname + url.search);
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
